.composer {
	pointer-events: auto;
	width: 100%;
	box-sizing: border-box;
	display: grid;
	/* Must sit above the gradient scrim (which is opaque at its bottom edge),
	   otherwise the pill/card is painted over and appears invisible. */
	position: relative;
	z-index: 1;
	background: var(--parrot-chat-composer-bg);
	border: 1px solid var(--parrot-chat-composer-border);
	box-shadow: var(--parrot-chat-composer-shadow);
	transition: border-radius 0.18s ease, box-shadow 0.2s, border-color 0.2s;
}

.composer:focus-within {
	border-color: var(--parrot-chat-accent);
	box-shadow: var(--parrot-chat-composer-shadow), 0 0 0 3px var(--parrot-chat-accent-glow);
}

.pill {
	grid-template-columns: auto 1fr auto;
	grid-template-areas: "attach text cluster";
	align-items: center;
	gap: 4px;
	padding: 5px 6px;
	border-radius: var(--parrot-chat-composer-radius);
}

.card {
	position: relative;
	grid-template-columns: auto 1fr auto;
	grid-template-rows: auto auto auto;
	/* Chips and text each span the full width; the controls sit on the bottom row.
	   The expand button is floated top-right (see .expandBtn) rather than taking a track. */
	grid-template-areas:
		"chips chips chips"
		"text text text"
		"attach . cluster";
	align-items: center;
	gap: 6px 4px;
	padding: 10px 10px 8px;
	border-radius: var(--parrot-chat-composer-radius-card);
}

/* Room for the floated expand button so text never runs under it. */
.card .textarea {
	padding-right: 34px;
}

.full {
	flex: 1;
	align-self: stretch;
	min-height: 0;
	grid-template-rows: auto 1fr auto;
}

.textarea {
	grid-area: text;
	flex: 1;
	width: 100%;
	min-width: 0;
	box-sizing: border-box;
	padding: 10px 12px;
	border: none;
	outline: none;
	background: transparent;
	color: var(--parrot-chat-text);
	font-family: inherit;
	font-size: var(--parrot-chat-font-size-md);
	line-height: 20px;
	resize: none;
	max-height: 45vh;
	overflow-y: auto;
}

.full .textarea {
	grid-area: text;
	height: 100%;
	max-height: none;
}

/* Empty state: keep the placeholder on a single line so a narrow pill can't wrap/clip it. */
.singleLine {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.textarea::placeholder {
	color: var(--parrot-chat-text-muted);
}

.textarea:disabled {
	color: var(--parrot-chat-text-muted);
	cursor: not-allowed;
}

.cluster {
	grid-area: cluster;
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
}

.attachArea {
	grid-area: attach;
	display: flex;
	align-items: center;
}

.iconBtn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	padding: 0;
	flex-shrink: 0;
	background: transparent;
	border: none;
	border-radius: 50%;
	color: var(--parrot-chat-text-muted);
	cursor: pointer;
	transition: color 0.12s, background 0.12s;
}

.iconBtn:hover {
	color: var(--parrot-chat-text);
	background: var(--parrot-chat-menu-hover);
}

.iconBtn svg {
	width: 20px;
	height: 20px;
	fill: none;
	stroke: currentColor;
	stroke-width: 2;
	stroke-linecap: round;
	stroke-linejoin: round;
}

.expandBtn {
	position: absolute;
	top: 8px;
	right: 8px;
}

.sendBtn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	flex-shrink: 0;
	background: var(--parrot-chat-user-bubble);
	border: none;
	border-radius: 50%;
	color: #ffffff;
	cursor: pointer;
	box-shadow: var(--parrot-chat-shadow);
	/* Note: `background` is intentionally NOT transitioned — interpolating a gradient to
	   a solid color (the stop state) leaves it stuck transparent. Hover fades via filter. */
	transition: box-shadow 0.2s ease, filter 0.2s ease, transform 0.15s ease, opacity 0.2s ease;
}

.sendBtn:hover:not(:disabled) {
	filter: brightness(1.1);
	box-shadow: 0 2px 14px var(--parrot-chat-accent-glow);
}

.sendBtn:active:not(:disabled) {
	transform: scale(0.94);
}

.sendBtn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

/* Stop (while a response is generating): neutral, non-accent, filled square. */
.stop {
	background: var(--parrot-chat-surface-raised);
	color: var(--parrot-chat-text);
	box-shadow: none;
}

.stop:hover:not(:disabled) {
	background: var(--parrot-chat-border-strong);
	filter: none;
	box-shadow: none;
}

/* Higher specificity than `.sendBtn svg` so the stop glyph is filled, not outlined. */
.sendBtn.stop svg {
	fill: currentColor;
	stroke: none;
}

.sendBtn svg {
	width: 16px;
	height: 16px;
	fill: none;
	stroke: currentColor;
	stroke-width: 2.5;
	stroke-linecap: round;
	stroke-linejoin: round;
}

.inputWrapper {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 0;
	/* Sit above the gradient scrim (which is positioned and would otherwise paint
	   over the static disclaimer, hiding it entirely). */
	position: relative;
	z-index: 1;
}

/* Fullscreen compose: the wrapper must stretch to fill the composer dock so the
   composer's own `flex: 1` (.full) has real height to grow into. Without this the
   composer resolves flex against the wrapper's content height and collapses. */
.wrapperFull {
	flex: 1;
	min-height: 0;
	/* Intentional breathing room between the top of the fullscreen composer and the chat
	   header. The dock already starts below the header (top: --parrot-chat-header-height);
	   this is an additional deliberate gap so the expanded card doesn't butt against it. */
	margin-top: 30px;
}

.inputDisclaimer {
	padding: 4px 10px 0 10px;
	font-size: 10px;
	color: var(--parrot-chat-text-muted);
	line-height: 1.2;
	text-align: center;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

